import React, { useState } from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/contactus.scss";

function Contact() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const contact = siteConfig.contact || {};
  const address = Array.isArray(contact.address)
    ? contact.address.join(", ")
    : contact.address || contact.addressStatus;

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
    setShowSuccess(false);

    if (errors[name]) {
      setErrors((previous) => ({ ...previous, [name]: "" }));
    }
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.firstName.trim()) nextErrors.firstName = "Enter your first name.";
    if (!formData.lastName.trim()) nextErrors.lastName = "Enter your last name.";

    if (!formData.email.trim()) {
      nextErrors.email = "Enter your email address.";
    } else if (!isValidEmail(formData.email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!formData.subject) nextErrors.subject = "Choose a subject.";

    if (!formData.message.trim()) {
      nextErrors.message = "Tell us how we can help.";
    } else if (formData.message.trim().length < 10) {
      nextErrors.message = "Please include at least 10 characters.";
    }

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formErrors = validateForm();

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      const firstErrorField = Object.keys(formErrors)[0];
      const element = document.getElementById(firstErrorField);

      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    await new Promise((resolve) => window.setTimeout(resolve, 900));
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      subject: "",
      message: "",
    });
    setIsSubmitting(false);
    setShowSuccess(true);
  };

  return (
    <main className="contact-page">
      <header className="contact-hero">
        <p className="contact-eyebrow">Customer care</p>
        <h1>We&apos;re here to help</h1>
        <p>
          Questions about a ritual, an ingredient or an order? Share the
          details below and our care team will be in touch.
        </p>
      </header>

      <section className="contact-layout" aria-label="Contact customer care">
        <aside className="contact-details">
          <p className="contact-eyebrow">Talk with us</p>
          <h2>Thoughtful care, from our team to you.</h2>
          <p>
            We usually reply during {contact.hours}. For order questions,
            please include the email used at checkout and your order number.
          </p>

          <dl>
            <div>
              <dt>Email</dt>
              <dd>
                <a href={`mailto:${contact.email}`}>{contact.email}</a>
              </dd>
            </div>
            <div>
              <dt>Country / region</dt>
              <dd>{contact.country}</dd>
            </div>
            <div>
              <dt>Business address</dt>
              <dd>{address}</dd>
            </div>
          </dl>

          <p className="contact-details__note">
            Looking for delivery or return information? Visit our{" "}
            <Link to="/shipping">shipping guide</Link> or{" "}
            <Link to="/returns">returns policy</Link>.
          </p>
        </aside>

        <div className="contact-form-wrap">
          {showSuccess && (
            <div className="contact-success" role="status" aria-live="polite">
              <div>
                <strong>Thank you for reaching out.</strong>
                <p>
                  Your message has been received. A member of the {siteConfig.brandName}{" "}
                  care team will reply soon.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowSuccess(false)}
                aria-label="Dismiss confirmation"
              >
                ×
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate aria-busy={isSubmitting}>
            <div className="contact-form__row">
              <div className="contact-field">
                <label htmlFor="firstName">First name *</label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.firstName)}
                  aria-describedby={errors.firstName ? "firstName-error" : undefined}
                  required
                />
                {errors.firstName && (
                  <span id="firstName-error" className="contact-error" role="alert">
                    {errors.firstName}
                  </span>
                )}
              </div>

              <div className="contact-field">
                <label htmlFor="lastName">Last name *</label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.lastName)}
                  aria-describedby={errors.lastName ? "lastName-error" : undefined}
                  required
                />
                {errors.lastName && (
                  <span id="lastName-error" className="contact-error" role="alert">
                    {errors.lastName}
                  </span>
                )}
              </div>
            </div>

            <div className="contact-form__row">
              <div className="contact-field">
                <label htmlFor="email">Email address *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "email-error" : undefined}
                  required
                />
                {errors.email && (
                  <span id="email-error" className="contact-error" role="alert">
                    {errors.email}
                  </span>
                )}
              </div>

            </div>

            <div className="contact-field">
              <label htmlFor="subject">How can we help? *</label>
              <div className="contact-select-wrap">
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.subject)}
                  aria-describedby={errors.subject ? "subject-error" : undefined}
                  required
                >
                  <option value="">Select a subject</option>
                  <option value="order">Order support</option>
                  <option value="ritual">Product and ritual advice</option>
                  <option value="returns">Returns and exchanges</option>
                  <option value="wholesale">Wholesale</option>
                  <option value="press">Press and partnerships</option>
                  <option value="other">Something else</option>
                </select>
              </div>
              {errors.subject && (
                <span id="subject-error" className="contact-error" role="alert">
                  {errors.subject}
                </span>
              )}
            </div>

            <div className="contact-field">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                rows="7"
                value={formData.message}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.message)}
                aria-describedby={errors.message ? "message-error" : "message-help"}
                required
              />
              <span id="message-help" className="contact-help">
                Please do not include payment details or sensitive information.
              </span>
              {errors.message && (
                <span id="message-error" className="contact-error" role="alert">
                  {errors.message}
                </span>
              )}
            </div>

            <button className="contact-submit" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

export default Contact;
